import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchStreamlinedClients,
  deleteClient,
  fetchClientById,
} from "../../store/thunks/clientsThunk";
import ConfirmDeleteDialog from "../../dialogs/ConfirmDeleteDialog";
import ClientOverview from "./ClientOverview";
import Table from "../../applicationUi/components/Table";
import { format, differenceInYears } from "date-fns";
import { logInfo, logError } from "../../utils/logger";
import { clearSelectedClinicians } from "../../store/slices/cliniciansSlice";
import {
  clearSelectedClient,
  setClient,
} from "../../store/slices/clientsSlice";
import { setClickedItem } from "../../store/slices/appStateSlice";
import QuickAddClientModal from "../../modals/QuickAddClientModal";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import AppointmentSummary from "../../applicationUi/components/appointments/AppointmentSummary";
import { setSelectedAppointment } from "../../store/slices/appointmentsSlice";

export default function ClientsPage({ clickedItem: propClickedItem }) {
  const dispatch = useDispatch();
  
  // Get clickedItem from Redux state (preferred) or fallback to props
  const reduxClickedItem = useSelector((state) => state.appState.clickedItem);
  const clickedItem = reduxClickedItem || propClickedItem;
  const {
    clients = [],
    pagination = {
      currentPage: 1,
      pageSize: 20,
      totalClients: 0,
      totalPages: 0,
    },
    status: clientStatus,
    error: clientError,
    selectedClient,
  } = useSelector((state) => state.clients);

  const [isDeleteClientModalOpen, setIsDeleteClientModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });
  const [isQuickAddClientModalOpen, setIsQuickAddClientModalOpen] =
    useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [previousSearch, setPreviousSearch] = useState("");
  const [isWideSidePanelOpen, setIsWideSidePanelOpen] = useState(false);
  
  // Get selected appointment from Redux for global modal
  const selectedAppointment = useSelector((state) => state.appointments.selectedAppointment);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchFilter);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchFilter]);

  useEffect(() => {
    console.log("ClientsPage clickedItem effect", { clickedItem });
    if (clickedItem && clickedItem._id) {
      logInfo("Fetching specific client", { clientId: clickedItem._id });
      console.log("ClientsPage: Fetching client", clickedItem._id);
      dispatch(fetchClientById(clickedItem._id));
    } else {
      const searchChanged = debouncedSearch !== previousSearch;
      const shouldResetPage = sortConfig.key || searchChanged;
      
      logInfo("Fetching clients", {
        page: shouldResetPage ? 1 : pagination.currentPage,
        limit: pagination.pageSize,
        sortKey: sortConfig.key,
        sortDirection: sortConfig.direction,
        search: debouncedSearch,
        searchChanged,
      });
      
      dispatch(
        fetchStreamlinedClients({
          page: shouldResetPage ? 1 : pagination.currentPage, // Reset page only if sorting or search term changed
          limit: pagination.pageSize,
          sort: sortConfig.key
            ? {
                [sortConfig.key]: sortConfig.direction === "ascending" ? 1 : -1,
              }
            : undefined,
          search: debouncedSearch,
        })
      );
      
      // Update previous search after dispatch
      if (searchChanged) {
        setPreviousSearch(debouncedSearch);
      }
    }
  }, [
    dispatch,
    pagination.currentPage,
    pagination.pageSize,
    clickedItem,
    sortConfig.key,
    sortConfig.direction,
    debouncedSearch,
    previousSearch,
  ]);

  const handleRowClick = (clientId) => {
    logInfo("Client row clicked", { clientId: clientId });
    dispatch(fetchClientById(clientId));
  };

  // Handle appointment click from ClientAppointments component
  const handleAppointmentClick = (appointment) => {
    console.log('[Clients Page] Appointment clicked:', appointment);
    dispatch(setSelectedAppointment(appointment));
    setIsWideSidePanelOpen(true);
  };

  // Handle closing the appointment summary modal
  const handleCloseWideSidePanel = () => {
    console.log('[Clients Page] Closing appointment summary');
    // AppointmentSummary will handle most cleanup, we just close the modal
    setIsWideSidePanelOpen(false);
  };



  const handleBackToClients = () => {
    logInfo("Navigating back to client list");
    dispatch(clearSelectedClient());
    dispatch(clearSelectedClinicians());
  };

  const handleSortChange = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    console.log("[CLIENTS PAGE] Sorting by:", key, "in direction:", direction);

    setSortConfig({ key, direction });
    dispatch(
      fetchStreamlinedClients({
        page: 1, // Reset to the first page
        limit: pagination.pageSize,
        sort: { [key]: direction === "ascending" ? 1 : -1 },
        search: debouncedSearch,
      })
    );
  };

  const formattedClients =
    clients?.map((client) => {
      const dateOfBirth = client.dateOfBirth
        ? new Date(client.dateOfBirth)
        : null;
      const formattedDateOfBirth = dateOfBirth
        ? format(dateOfBirth, "MMMM d, yyyy")
        : "N/A";
      const age = dateOfBirth
        ? differenceInYears(new Date(), dateOfBirth)
        : "N/A";

      const formattedPhoneNumbers = client.phoneNumbers
        ? client.phoneNumbers.map((phone) => `${phone.number}`).join(", ")
        : "N/A";

      return {
        ...client,
        dateOfBirth: formattedDateOfBirth,
        age,
        formattedPhoneNumbers,
      };
    }) || [];

  logInfo("Rendered ClientsPage", {
    clientCount: clients.length,
    searchFilter,
    selectedClient: selectedClient ? selectedClient._id : null,
    clientStatus,
    paginationInfo: pagination,
    sortConfig,
  });

  return (
    <div className="pb-8">
      {selectedClient ? (
        <div className="p-6">
          <ClientOverview onAppointmentClick={handleAppointmentClick} />
        </div>
      ) : (
        <>
          <div className="m-8">
            <div className="text-sm font-medium text-muted-foreground mb-2">
              Search clients
            </div>
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="w-full rounded-sm border border-gray-200 bg-white py-2 pl-10 pr-3 text-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder="Search by name, email, or phone number..."
                autoComplete="off"
              />
            </div>
          </div>

          <div className="px-8">
            {clientStatus === "loading" && <p>Loading clients...</p>}
            {clientStatus === "failed" && (
              <p>
                Error loading clients:{" "}
                {typeof clientError === "object"
                  ? JSON.stringify(clientError)
                  : clientError}
              </p>
            )}
            {clientStatus === "succeeded" && clients.length === 0 && !debouncedSearch && (
              <p>No clients available</p>
            )}
            {clientStatus === "succeeded" && clients.length === 0 && debouncedSearch && (
              <p>No clients match your search criteria</p>
            )}
            {clientStatus === "succeeded" && clients.length > 0 && (
              <Table
                columns={[
                  { header: "First Name", accessor: "firstName", sortable: true },
                  { header: "Last Name", accessor: "lastName", sortable: true },
                  {
                    header: "Date of Birth",
                    accessor: "dateOfBirth",
                  },
                  { header: "Age", accessor: "age" },
                  { header: "Email", accessor: "email" },
                  {
                    header: "Phone Numbers",
                    accessor: "formattedPhoneNumbers",
                  },
                ]}
                data={formattedClients}
                onRowClick={(row) => handleRowClick(row)}
                sortConfig={sortConfig}
                onSortChange={handleSortChange}
                actions={[
                  {
                    label: "Add Client",
                    onClick: () => {
                      logInfo("Add Client button clicked");
                      setIsQuickAddClientModalOpen(true);
                    },
                  },
                ]}
                title="Clients"
                description={debouncedSearch 
                  ? `Showing ${pagination.totalClients} clients matching "${debouncedSearch}"`
                  : "A list of all clients. Use pagination to view more."
                }
                showPagination={pagination.totalPages > 1}
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalResults={pagination.totalClients}
                pageSize={pagination.pageSize}
                onPageChange={(page) => {
                  logInfo("Pagination changed", {
                    newPage: page,
                  });
                  dispatch(
                    fetchStreamlinedClients({
                      page,
                      limit: pagination.pageSize,
                      sort: sortConfig.key
                        ? {
                            [sortConfig.key]:
                              sortConfig.direction === "ascending" ? 1 : -1,
                          }
                        : undefined,
                      search: debouncedSearch,
                    })
                  );
                }}
              />
            )}
          </div>
        </>
      )}

      {isDeleteClientModalOpen && (
        <ConfirmDeleteDialog
          open={isDeleteClientModalOpen}
          setOpen={setIsDeleteClientModalOpen}
          onDelete={() => {
            logInfo("Deleting client", {
              clientId: clientToDelete,
            });
            dispatch(deleteClient(clientToDelete));
          }}
        />
      )}

      {isQuickAddClientModalOpen && (
        <QuickAddClientModal
          onClose={() => setIsQuickAddClientModalOpen(false)}
        />
      )}
      
      {/* Global AppointmentSummary modal for ClientsPage */}
      {selectedAppointment && (
        <AppointmentSummary
          title={"Appointment Summary"}
          open={isWideSidePanelOpen}
          onClose={handleCloseWideSidePanel}
          appointment={selectedAppointment}
          onAppointmentClick={handleAppointmentClick}
          preventClientAppointmentsClear={true}
        />
      )}
    </div>
  );
}
